import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {environment} from "@env/environment";
import {
  CardReader,
  DistrictGridSpacesForTerm,
  LeaseGridSpace,
  LeasePricingPeriod,
  LeaseStatus,
  LeaseSummary,
  LeaseSupplierState,
  PaymentConfigRequest,
  PaymentMethod,
  SpaceSelection
} from "../leasing.model";
import {GenericService} from "@ui-kit/framework/common/generic.service";
import {PosRequest} from '@features/support/request-pos-dialog/request-pos.model';
import {SpaceDefaultRate, TimePeriod} from "../../spaces/model/space-default-pricing.interface";

@Injectable({
  providedIn: 'root'
})
export class LeaseViewService extends GenericService<LeaseSummary> {
  constructor(http: HttpClient) {
    super(http, environment.apiLeaseHost + `/console/manage-lease`);
  }

  refreshLeaseData(leaseId: string): Observable<LeaseSummary> {
    return this.http.get<LeaseSummary>(this.getBaseUrl + `/${leaseId}/refresh`);
  }

  getLeaseSpaces(leaseId: string): Observable<LeaseGridSpace[]> {
    return this.http.get<LeaseGridSpace[]>(this.getBaseUrl + `/${leaseId}/spaces`);
  }

  saveLeaseSpaces(request: SpaceSelection, leaseId: string): Observable<LeaseSummary> {
    return this.http.post<LeaseSummary>(this.getBaseUrl + `/${leaseId}/spaces`, request);
  }

  removeLeaseSpaces(request: SpaceSelection, leaseId: string, spaceUuids: string[]) {
    let requestParams = new HttpParams()
      .set('retailerId', request.retailerId!)
      .set('selectedFloorSpaceNames', request.selectedFloorSpaceNames.toString())
      .set('districtUuid', request.districtUuid!)
      .set('leaseId', request.leaseUuid!)
      .set('spaceUuids', spaceUuids.toString());

    return this.http.delete<void>(this.getBaseUrl + `/${leaseId}/spaces`, {params: requestParams});
  }

  createPlannedLease(lease: SpaceSelection): Observable<LeaseSummary> {
    return this.http.post<LeaseSummary>(this.getBaseUrl, lease);
  }

  // New: suggested rates for selected period and spaces
  getSuggestedRates(payload: { startDate: string; endDate: string | null; timePeriod: TimePeriod; spaceIds: number[] }): Observable<SpaceDefaultRate[]> {
    let params = new HttpParams()
      .set('startDate', payload.startDate)
      .set('timePeriod', payload.timePeriod);
    if (payload.endDate) params = params.append('endDate', payload.endDate);
    if (payload.spaceIds?.length) params = params.append('spaceIds', payload.spaceIds.join(','));
    return this.http.get<SpaceDefaultRate[]>(this.getBaseUrl + `/suggested-rates`, { params });
  }

  getDistrictSpacesForDateRange(startDate: string, endDate: string | null): Observable<DistrictGridSpacesForTerm> {
    let requestParams = new HttpParams()
      .set('startDate', startDate);

    if (endDate) {
      requestParams = requestParams.append('endDate', endDate);
    }

    return this.http.get<DistrictGridSpacesForTerm>(this.getBaseUrl + `/grid-spaces`, {params: requestParams});
  }

  requestLeaseApproval(leaseId: string): Observable<void> {
    return this.http.post<void>(this.getBaseUrl + `/${leaseId}/notify/approval`, null);
  }

  updateLeaseStatus(retailerId: number, leaseId: string, newStatus: LeaseStatus): Observable<LeaseSummary> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post<LeaseSummary>(this.getBaseUrl + `/${leaseId}/update-status`, `"${newStatus}"`, options);
  }

  removeLease(retailerId: number, leaseId: string) {
    return this.http.delete<void>(this.getBaseUrl + `/${leaseId}`);
  }

  getLeasePaymentConfigHistory(leaseId: string): Observable<PaymentConfigRequest[]> {
    return this.http.get<PaymentConfigRequest[]>(this.getBaseUrl + `/${leaseId}/payment-config`);
  }

  getLeaseCurrentPaymentConfig(leaseUuid: string): Observable<PaymentConfigRequest> {
    return this.http.get<PaymentConfigRequest>(this.getBaseUrl + `/${leaseUuid}/payment-config/current`);
  }

  getPaymentMethods(leaseId: string): Observable<PaymentMethod[]> {
    return this.http.get<PaymentMethod[]>(this.getBaseUrl + `/${leaseId}/payment-config/payment-methods`)
  }

  savePaymentConfig(request: PaymentConfigRequest, leaseId: string): Observable<PaymentConfigRequest> {
    return this.http.post<PaymentConfigRequest>(this.getBaseUrl + `/${leaseId}/payment-config`, request);
  }

  getLeaseSuppliers(leaseId: string, paymentConfigId: string) {
    return this.http.get<LeaseSupplierState>(this.getBaseUrl + `/${leaseId}/payment-config/${paymentConfigId}/suppliers`)
  }

  updatePaymentConfigStatus(paymentConfigUuid: string, leaseId: string, request: PosRequest): Observable<PaymentConfigRequest> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post<PaymentConfigRequest>(this.getBaseUrl + `/${leaseId}/payment-config/status/${paymentConfigUuid}`, request, options);
  }

  getAssignedLeaseCardReaders(leaseId: string): Observable<CardReader[]> {
    return this.http.get<CardReader[]>(this.getBaseUrl + `/${leaseId}/card-readers/assigned`)
  }

  getAvailableLeaseCardReaders(leaseId: string, startDate: string, endDate: string): Observable<CardReader[]> {
    let requestParams = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    return this.http.get<CardReader[]>(this.getBaseUrl + `/${leaseId}/card-readers/available`, {params: requestParams})
  }

  assignLeaseCardReader(leaseId: string, request: CardReader): Observable<CardReader> {
    return this.http.post<CardReader>(this.getBaseUrl + `/${leaseId}/card-readers`, request)
  }

  removeLeaseCardReader(leaseId: string, cardReaderId: any): Observable<void> {
    return this.http.delete<void>(this.getBaseUrl + `/${leaseId}/card-readers/${cardReaderId}`)
  }

  requestBillingDetailsUpdate(retailerId: number): Observable<void> {
    return this.http.post<void>(environment.apiRetailersHost + `/console/manage-retailer/${retailerId}/billing/request`, null)
  }

  updateBillingDetailsRequested(leaseId: string): Observable<PaymentConfigRequest> {
    return this.http.post<PaymentConfigRequest>(this.getBaseUrl + `/${leaseId}/payment-config/billing-details`, null);
  }

  get getBaseUrl() {
    return environment.apiLeaseHost + '/console/manage-lease'
  }

  getAllLeasePricingPeriods(leaseUuid: string): Observable<LeasePricingPeriod[]> {
    return this.http.get<LeasePricingPeriod[]>(this.getBaseUrl + `/${leaseUuid}/pricing/all`)
  }

  getLeaseCurrentPricingPeriod(leaseUuid: string): Observable<LeasePricingPeriod> {
    return this.http.get<LeasePricingPeriod>(this.getBaseUrl + `/${leaseUuid}/pricing/current`)
  }

  createNewLeasePricingPeriod( leaseUuid: string, leasePricingPeriod: LeasePricingPeriod): Observable<LeasePricingPeriod> {
    return this.http.post<LeasePricingPeriod>(this.getBaseUrl + `/${leaseUuid}/pricing`, leasePricingPeriod);
  }

  removePaymentConfigFromLease(paymentConfigUuid: string, leaseUuid: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + `/${leaseUuid}/payment-config/${paymentConfigUuid}`);
  }

  removePricingPeriod(leaseUuid: string, selectedPricingPeriodUuid: string): Observable<void> {
    return this.http.delete<void>(this.getBaseUrl + `/${leaseUuid}/pricing/${selectedPricingPeriodUuid}`);
  }

  updateLeasePricingPeriod(leaseUuid: string, periodToSave: LeasePricingPeriod): Observable<LeasePricingPeriod> {
    return this.http.post<LeasePricingPeriod>(this.getBaseUrl + `/${leaseUuid}/pricing/${periodToSave.uuid}`, periodToSave);
  }
}